import React, { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import testimonials from "../../../apiData/testimonials.json";
import {
  Section, BgBlob, DotGrid, Inner,
  HeaderWrap, Eyebrow, SectionTitle, SectionDesc, Divider,
  TrustBar, TrustStat, TrustNumber, TrustLabel, TrustDivider,
  CarouselViewport, CardsGrid, Card, VerifiedChip, QuoteMark,
  AvatarRing, AvatarInner, AvatarImg, AvatarFallback,
  ClientName, ClientRole, CardText, ExpandBtn, Stars,
  CtaBanner, BannerOrb, BannerTitle, BannerDesc, BannerStars, BannerBtn,
  ScrollHint,
} from "./Testimonials.styles";

// ─── Icon atoms ────────────────────────────────────────────────────────────────

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const VerifiedIcon = () => (
  <svg viewBox="0 0 16 16" fill="currentColor" width="10" height="10" aria-hidden="true">
    <path d="M8 0L9.8 2.6L13 1.1L13.4 4.5L16.8 5.2L15 8L16.8 10.8L13.4 11.5L13 14.9L9.8 13.4L8 16L6.2 13.4L3 14.9L2.6 11.5L-0.8 10.8L1 8L-0.8 5.2L2.6 4.5L3 1.1L6.2 2.6L8 0Z" />
  </svg>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────

const MAX_CHARS = 160;

const getInitials = (name = "") =>
  name.split(" ").slice(0, 2).map((n) => n[0] ?? "").join("").toUpperCase();

// ─── Star rating ───────────────────────────────────────────────────────────────

const StarRating = ({ count = 5 }) => (
  <Stars aria-label={`${count} out of 5 stars`}>
    {Array.from({ length: 5 }, (_, i) => (
      <span key={i} aria-hidden="true">{i < count ? "★" : "☆"}</span>
    ))}
  </Stars>
);

// ─── TestimonialCard ──────────────────────────────────────────────────────────

const FALLBACK_EMOJIS = ["🧘", "🌿", "🪷", "☀️", "🌊", "✨"];

const TestimonialCard = ({ item, index }) => {
  const [imgError, setImgError] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const isLong    = (item.comments?.length ?? 0) > MAX_CHARS;
  const displayText = !isLong || expanded
    ? item.comments
    : `${item.comments?.slice(0, MAX_CHARS)}…`;

  return (
    <Card $index={index} aria-label={`Testimonial from ${item.name}`}>
      <VerifiedChip><VerifiedIcon /> Verified</VerifiedChip>
      <QuoteMark aria-hidden="true">"</QuoteMark>

      <AvatarRing $index={index} aria-hidden="true">
        <AvatarInner>
          {item.image && !imgError ? (
            <AvatarImg src={item.image} alt={item.name} onError={() => setImgError(true)} />
          ) : (
            <AvatarFallback>
              {item.image
                ? FALLBACK_EMOJIS[index % FALLBACK_EMOJIS.length]
                : getInitials(item.name)}
            </AvatarFallback>
          )}
        </AvatarInner>
      </AvatarRing>

      <ClientName>{item.name}</ClientName>
      <ClientRole>{item.profession}</ClientRole>
      <StarRating count={item.rating ?? 5} />
      <CardText>{displayText}</CardText>

      {isLong && (
        <ExpandBtn
          type="button"
          onClick={() => setExpanded((e) => !e)}
          aria-expanded={expanded}
        >
          {expanded ? "Show less ↑" : "Read more ↓"}
        </ExpandBtn>
      )}
    </Card>
  );
};

// ─── useAutoScroll — RAF-based, pauses on hover / touch / drag ───────────────

const SCROLL_SPEED = 0.6; // px per frame  (~36px/s at 60fps) — readable pace

function useAutoScroll() {
  const trackRef  = useRef(null);
  const rafRef    = useRef(null);
  const pausedRef = useRef(false); // hover/touch pauses animation
  const dragRef   = useRef({ active: false, startX: 0, scrollLeft: 0 });

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    // ── RAF loop ──
    const tick = () => {
      if (!pausedRef.current && el) {
        el.scrollLeft += SCROLL_SPEED;
        // seamless loop: when we reach the halfway point (duplicated list),
        // jump back to 0 without visual glitch
        if (el.scrollLeft >= el.scrollWidth / 2) {
          el.scrollLeft = 0;
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    // ── Hover pause ──
    const pause  = () => { pausedRef.current = true;  };
    const resume = () => { pausedRef.current = false; };
    el.addEventListener("mouseenter", pause);
    el.addEventListener("mouseleave", resume);

    // ── Touch pause (mobile) ──
    const touchStart = () => { pausedRef.current = true;  };
    const touchEnd   = () => { pausedRef.current = false; };
    el.addEventListener("touchstart", touchStart, { passive: true });
    el.addEventListener("touchend",   touchEnd,   { passive: true });

    // ── Click-drag scroll (desktop) ──
    const mouseDown = (e) => {
      dragRef.current = { active: true, startX: e.pageX - el.offsetLeft, scrollLeft: el.scrollLeft };
      pausedRef.current = true;
      el.style.cursor = "grabbing";
    };
    const mouseMove = (e) => {
      if (!dragRef.current.active) return;
      e.preventDefault();
      const x    = e.pageX - el.offsetLeft;
      const walk = (x - dragRef.current.startX) * 1.2;
      el.scrollLeft = dragRef.current.scrollLeft - walk;
    };
    const mouseUp = () => {
      dragRef.current.active = false;
      pausedRef.current = false;
      el.style.cursor = "grab";
    };
    el.addEventListener("mousedown", mouseDown);
    el.addEventListener("mousemove", mouseMove);
    el.addEventListener("mouseup",   mouseUp);
    el.addEventListener("mouseleave", mouseUp);

    return () => {
      cancelAnimationFrame(rafRef.current);
      el.removeEventListener("mouseenter",  pause);
      el.removeEventListener("mouseleave",  resume);
      el.removeEventListener("touchstart",  touchStart);
      el.removeEventListener("touchend",    touchEnd);
      el.removeEventListener("mousedown",   mouseDown);
      el.removeEventListener("mousemove",   mouseMove);
      el.removeEventListener("mouseup",     mouseUp);
    };
  }, []);

  return trackRef;
}

// ─── Testimonials ─────────────────────────────────────────────────────────────

const Testimonials = () => {
  const navigate   = useNavigate();
  const trackRef   = useAutoScroll();

  // Duplicate the list so the loop feels seamless
  const items = testimonials ?? [];
  const doubled = [...items, ...items];

  const handleCtaClick = useCallback(() => {
    navigate("/contactUs");
  }, [navigate]);

  return (
    <Section id="testimonials">
      <BgBlob className="b1" aria-hidden="true" />
      <BgBlob className="b2" aria-hidden="true" />
      <BgBlob className="b3" aria-hidden="true" />
      <DotGrid aria-hidden="true" />

      <Inner>
        {/* ── Header ── */}
        <HeaderWrap>
          <Eyebrow>✦ Client Stories</Eyebrow>
          <SectionTitle>What Our Clients Say</SectionTitle>
          <Divider aria-hidden="true" />
          <SectionDesc>
            Real words from real students who found balance, strength, and
            serenity through Yoga Shastra. Every journey is unique — these are
            the moments that inspire us to keep teaching.
          </SectionDesc>
        </HeaderWrap>

        {/* ── Trust stats strip ── */}
        <TrustBar aria-label="Key statistics">
          <TrustStat>
            <TrustNumber>2,400+</TrustNumber>
            <TrustLabel>Happy Students</TrustLabel>
          </TrustStat>
          <TrustDivider aria-hidden="true" />
          <TrustStat>
            <TrustNumber>4.9★</TrustNumber>
            <TrustLabel>Average Rating</TrustLabel>
          </TrustStat>
          <TrustDivider aria-hidden="true" />
          <TrustStat>
            <TrustNumber>98%</TrustNumber>
            <TrustLabel>Would Recommend</TrustLabel>
          </TrustStat>
          <TrustDivider aria-hidden="true" />
          <TrustStat>
            <TrustNumber>8 Yrs</TrustNumber>
            <TrustLabel>Of Practice</TrustLabel>
          </TrustStat>
        </TrustBar>

        {/* ── Auto-scroll carousel ── */}
        <CarouselViewport>
          <CardsGrid ref={trackRef} aria-label="Client testimonials">
            {doubled.map((item, i) => (
              <TestimonialCard
                key={`${item.name}_${i}`}
                item={item}
                index={i % items.length}
                aria-hidden={i >= items.length} /* hide dupes from screen readers */
              />
            ))}
          </CardsGrid>
        </CarouselViewport>
        <ScrollHint aria-hidden="true">← Drag or swipe to explore →</ScrollHint>

        {/* ── Bottom CTA banner ── */}
        <CtaBanner>
          <BannerOrb className="o1" aria-hidden="true" />
          <BannerOrb className="o2" aria-hidden="true" />
          <BannerStars aria-label="4.9 out of 5 stars from 500 reviews">
            {"★★★★★".split("").map((s, i) => (
              <span key={i} aria-hidden="true">{s}</span>
            ))}
            <em aria-hidden="true">4.9 · 2,400+ reviews</em>
          </BannerStars>
          <BannerTitle>Join the Community — Your Mat Awaits</BannerTitle>
          <BannerDesc>
            Step onto the mat and discover a practice that meets you exactly
            where you are. Nurturing your body, calming your mind, and
            elevating your spirit — one breath at a time.
          </BannerDesc>
          <BannerBtn
            type="button"
            onClick={handleCtaClick}
            aria-label="Book an online yoga class"
          >
            Book Online Yoga Class <ArrowIcon />
          </BannerBtn>
        </CtaBanner>
      </Inner>
    </Section>
  );
};

Testimonials.displayName = "Testimonials";
export default React.memo(Testimonials);